import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Home,
  Video,
  Building,
  Clock,
  Calendar,
  MapPin,
  Phone,
  Star,
  CalendarClock,
  Eye,
  XCircle,
} from 'lucide-react';
import { formatDate, getInitials } from '../lib/utils';

const genBookingId = (apt) => {
  const prefix = apt.service_type === 'home_visit' ? 'HV' : apt.service_type === 'online' ? 'ON' : 'CL';
  const num = (apt.id || '').replace(/\D/g, '').slice(0, 5).padStart(5, '0');
  return `PHY-${prefix}-${num}`;
};

export const PatientAppointmentCard = ({
  apt,
  onView,
  onReschedule,
  onCancel,
  onReview,
  isReviewed,
  cancelConfirmId,
  onCancelConfirm,
  onCancelDismiss,
}) => {
  const isHome = apt.service_type === 'home_visit';
  

  /*const isOnline = apt.service_type === 'online';*/
  const typeLabel = isHome ? 'Home Visit' /*: isOnline ? 'Online Consultation' */: 'Clinic Visit';
  const TypeIcon = isHome ? Home /*: isOnline ? Video*/ : Building;

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800 border-amber-200',
    confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    completed: 'bg-blue-100 text-blue-800 border-blue-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
    rejected: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  const headerColors = {
    pending: 'from-amber-500 to-amber-600',
    confirmed: 'from-emerald-500 to-emerald-600',
    completed: 'from-blue-500 to-blue-600',
    cancelled: 'from-red-500 to-red-600',
    rejected: 'from-slate-400 to-slate-500',
  };

  const isUpcoming = ['pending', 'confirmed'].includes(apt.status);

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`patient-apt-card-${apt.id}`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${headerColors[apt.status] || headerColors.pending} px-4 py-2.5 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <TypeIcon className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-semibold">{typeLabel}</span>
        </div>
        <span className="text-white/80 text-xs font-mono" data-testid={`apt-booking-id-${apt.id}`}>{genBookingId(apt)}</span>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Provider Info */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#E0F2FE] flex items-center justify-center font-bold text-[#0F62FE]">
            {getInitials(apt.provider?.user?.name || 'Dr')}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900 truncate">{apt.provider?.user?.name || 'Healthcare Provider'}</p>
            <p className="text-xs text-slate-500 capitalize">{apt.provider?.profile?.specialization || apt.provider_type || ''}</p>
          </div>
          <Badge className={`${statusColors[apt.status] || ''} capitalize text-xs flex-shrink-0`}>{apt.status}</Badge>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm bg-slate-50 rounded-lg p-3">
          <div>
            <span className="text-slate-400 text-xs">Date</span>
            <p className="text-slate-800 font-medium flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-slate-400" />{formatDate(apt.appointment_date)}</p>
          </div>
          <div>
            <span className="text-slate-400 text-xs">Time</span>
            <p className="text-slate-800 font-medium flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" />{apt.time_slot}</p>
          </div>
          <div>
            <span className="text-slate-400 text-xs">Type</span>
            <p className="text-slate-800 font-medium flex items-center gap-1"><TypeIcon className="w-3.5 h-3.5 text-slate-400" />{typeLabel}</p>
          </div>
          <div>
            <span className="text-slate-400 text-xs">Fee</span>
            <p className="text-slate-800 font-medium">₹{apt.fee || 500}</p>
          </div>
          {isHome && apt.address && (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs">Address</span>
              <p className="text-slate-800 font-medium flex items-start gap-1"><MapPin className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />{apt.address}</p>
            </div>
          )}
          {apt.symptoms && (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs">Reason</span>
              <p className="text-slate-800 font-medium">{apt.symptoms}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onView(apt)} data-testid={`view-apt-${apt.id}`}>
            <Eye className="w-3.5 h-3.5 mr-1" />View Details
          </Button>

          {apt.provider?.user?.phone && (
            <a href={`tel:${apt.provider.user.phone}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" data-testid={`call-provider-${apt.id}`}>
              <Phone className="w-3.5 h-3.5" />Call
            </a>
          )}

          {isUpcoming && onReschedule && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onReschedule(apt)} data-testid={`reschedule-apt-${apt.id}`}>
              <CalendarClock className="w-3.5 h-3.5 mr-1" />Reschedule
            </Button>
          )}

          {isUpcoming && onCancel && (
            cancelConfirmId === apt.id ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-red-600 font-medium">Sure?</span>
                <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => onCancelConfirm(apt.id)} data-testid={`confirm-cancel-${apt.id}`}>Yes</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onCancelDismiss}>No</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs" onClick={() => onCancel(apt.id)} data-testid={`cancel-apt-${apt.id}`}>
                <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
              </Button>
            )
          )}

          {apt.status === 'completed' && !isReviewed && onReview && (
            <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50 h-8 text-xs" onClick={() => onReview(apt)} data-testid={`review-apt-${apt.id}`}>
              <Star className="w-3.5 h-3.5 mr-1" />Leave Review
            </Button>
          )}

          {apt.status === 'completed' && isReviewed && (
            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Reviewed</Badge>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientAppointmentCard;
